import * as React from "react";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import secureLocalStorage from "react-secure-storage";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import { createNewInsurance } from "../../actions/Insurance";
import EmptyState from "../../ui/EmptyState";
import PolicySummary from "../../ui/PolicySummary";
import { formatDate, formatMoney } from "../../utils/premium";

const isApproved = (row) => row.approved === true || row.approved === "true";

const columns = [
  { field: "policy_number", headerName: "Policy number", minWidth: 150, flex: 1 },
  { field: "description", headerName: "Description", minWidth: 180, flex: 1.4 },
  { field: "policy_type", headerName: "Policy type", minWidth: 140, flex: 1 },
  { field: "institution", headerName: "Institution", minWidth: 160, flex: 1.2 },
  {
    field: "cost",
    headerName: "Premium",
    type: "number",
    minWidth: 120,
    flex: 0.8,
    valueFormatter: ({ value }) => formatMoney(value),
  },
  {
    field: "daily_premium",
    headerName: "Daily premium",
    type: "number",
    minWidth: 120,
    flex: 0.8,
    valueFormatter: ({ value }) => formatMoney(value),
  },
  {
    field: "purchase_date",
    headerName: "Start date",
    minWidth: 120,
    valueFormatter: ({ value }) => formatDate(value),
  },
  {
    field: "maturity_date",
    headerName: "End date",
    minWidth: 120,
    valueFormatter: ({ value }) => formatDate(value),
  },
  {
    field: "status",
    headerName: "Status",
    minWidth: 120,
    valueGetter: ({ row }) => (isApproved(row) ? "Approved" : "Pending"),
    renderCell: ({ value }) => (
      <Chip
        size="small"
        label={value}
        color={value === "Approved" ? "success" : "warning"}
        variant="outlined"
      />
    ),
  },
];

const REQUIRED = ["policy_number", "policy_type", "institution", "cost", "tenure", "purchase_date"];

export default function AddNewInsurance() {
  const user = JSON.parse(secureLocalStorage.getItem("profile"));
  const uploadedBy = user.results.firstName + " " + user.results.lastName;
  const blank = {
    description: "",
    purchase_date: "",
    cost: "",
    institution: "",
    policy_number: "",
    policy_type: "",
    tenure: "",
  };
  const insurance = useSelector((state) => state.insurance) || [];
  const loaded = useSelector((state) => state.loaded.insurance);
  const dispatch = useDispatch();
  const [open, setOpen] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [touched, setTouched] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const [form, setForm] = React.useState(blank);

  const allowManipulate =
    user.results.assets === "Administrator" || user.results.assets === "Read-Write";

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });
  const missing = (key) => touched && !form[key];

  const dailyPremium = form.cost && form.tenure ? form.cost / form.tenure : "";
  const maturityDate =
    form.purchase_date && form.tenure
      ? moment(form.purchase_date).add(form.tenure, "days").format("YYYY-MM-DD")
      : "";

  const close = () => {
    setOpen(false);
    setTouched(false);
  };

  async function sendData() {
    setTouched(true);
    if (REQUIRED.some((key) => !form[key]) || Number(form.tenure) <= 0) return;
    setSaving(true);
    const saved = await dispatch(
      createNewInsurance({
        ...form,
        maturity_date: maturityDate,
        daily_premium: dailyPremium,
        uploaded_by: uploadedBy,
        approved: "false",
      })
    );
    setSaving(false);
    if (saved) {
      setForm(blank);
      close();
    }
  }

  const query = search.trim().toLowerCase();
  const rows = insurance
    .filter(
      (d) =>
        !query ||
        [d.policy_number, d.description, d.policy_type, d.institution]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(query))
    )
    .map((d) => ({ ...d, id: d._id }));

  return (
    <Box>
      <Stack direction={{ xs: "column", sm: "row" }} gap={1.5} justifyContent="space-between" sx={{ mb: 2 }}>
        <TextField
          placeholder="Search policy number, description, institution..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: { xs: "100%", sm: 380 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Tooltip title={allowManipulate ? "" : "You don't have permission to add insurance"}>
          <span>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<AddIcon />}
              disabled={!allowManipulate}
              onClick={() => setOpen(true)}
            >
              Add insurance
            </Button>
          </span>
        </Tooltip>
      </Stack>

      <Box sx={{ height: "calc(100vh - 330px)", minHeight: 420 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={!loaded}
          density="compact"
          disableRowSelectionOnClick
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
            sorting: { sortModel: [{ field: "purchase_date", sort: "desc" }] },
          }}
          pageSizeOptions={[10, 25, 50, 100]}
          slots={{
            noRowsOverlay: () => (
              <EmptyState
                title={query ? "No matching policies" : "No insurance recorded yet"}
                text={query ? "Try another search." : "Use “Add insurance” to record the first policy."}
              />
            ),
          }}
        />
      </Box>

      <Dialog open={open} onClose={saving ? undefined : close} maxWidth="sm" fullWidth>
        <DialogTitle>Add insurance</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField label="Description" fullWidth value={form.description} onChange={set("description")} autoFocus />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Policy number" required fullWidth value={form.policy_number} onChange={set("policy_number")} error={missing("policy_number")} helperText={missing("policy_number") && "Required"} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Policy type" required fullWidth value={form.policy_type} onChange={set("policy_type")} error={missing("policy_type")} helperText={missing("policy_type") && "Required"} />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Institution" required fullWidth value={form.institution} onChange={set("institution")} error={missing("institution")} helperText={missing("institution") && "Required"} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label="Premium" type="number" required fullWidth value={form.cost} onChange={set("cost")} error={missing("cost")} helperText={missing("cost") && "Required"} inputProps={{ min: 0, step: "0.01" }} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Tenure"
                type="number"
                required
                fullWidth
                value={form.tenure}
                onChange={set("tenure")}
                error={missing("tenure") || (touched && Number(form.tenure) <= 0)}
                helperText={missing("tenure") ? "Required" : "Number of days"}
                inputProps={{ min: 1, step: 1 }}
                InputProps={{ endAdornment: <InputAdornment position="end">days</InputAdornment> }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label="Start date" type="date" required fullWidth value={form.purchase_date} onChange={set("purchase_date")} InputLabelProps={{ shrink: true }} error={missing("purchase_date")} helperText={missing("purchase_date") && "Required"} />
            </Grid>
            <Grid item xs={12}>
              <PolicySummary
                items={[
                  { label: "Maturity date", value: maturityDate ? formatDate(maturityDate) : "—" },
                  { label: "Daily premium", value: dailyPremium ? formatMoney(dailyPremium) : "—" },
                ]}
              />
            </Grid>
          </Grid>
          {touched && REQUIRED.some((key) => !form[key]) && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Please fill in the required fields.
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={close} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={sendData} disabled={saving}>
            {saving ? "Submitting..." : "Submit for approval"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
